/**
 * Normalize incoming message payloads for cross-platform compatibility.
 * Handles differences between iOS, Android, and Web message structures.
 */
import { deriveCardMeta } from './derive-card-meta';
import { createLogger } from '~/utils/logger';

const logger = createLogger('normalize-payload');

/**
 * @param {Object | undefined | null} pack
 */
const normalizePackFields = (pack) => {
    if (!pack) return;

    if (pack.variant && typeof pack.variant === 'string') {
        pack.variant = pack.variant.toLowerCase();
    }
    deriveCardMeta(pack.cards);
};

/**
 * Normalize pack variant to lowercase.
 * iOS may send "STANDARD", Android sends "standard", web expects lowercase.
 *
 * Handles both data structures:
 * - Break data: data.breakInfo.box.pack.variant (used in StateManager)
 * - Async setup: { pack, outro } (async-experience)
 * - Pack data: data.variant (legacy async-experience)
 *
 * @param {Object} data - The data payload (can be break data or pack data)
 * @returns {Object} The normalized data
 */
export const normalizePackData = (data) => {
    if (!data) return data;

    // Handle full break data structure (data.breakInfo.box.pack.variant)
    const packFromBreakInfo = data.breakInfo?.box?.pack;
    normalizePackFields(packFromBreakInfo);

    if (data.pack) {
        normalizePackFields(data.pack);
        return data;
    }

    normalizePackFields(data);

    return data;
};

/**
 * Extract data payload from message with fallback for different structures.
 * Some platforms may wrap the payload differently.
 *
 * @param {Object} messageData - The raw message data from postMessage event
 * @returns {Object|undefined} The extracted data payload
 */
export const extractDataPayload = (messageData) => {
    if (!messageData) return undefined;

    // Standard structure: { type, data: { breakInfo, ... } }
    if (messageData.data !== undefined && messageData.data !== null) {
        return messageData.data;
    }

    // Fallback: maybe the entire message IS the data (minus type)
    // This handles cases where platforms don't wrap in 'data' key
    const { type, ...rest } = messageData;
    if (rest.breakInfo) {
        logger.warn('Using fallback data extraction', {
            event: 'message.payload_fallback',
            source: 'normalizePackData',
            reason: 'missing_data_wrapper',
            messageType: type,
        });
        return rest;
    }

    return undefined;
};
